import React, { useState, useEffect } from "react";
import { Modal, Form, Select, Input, Alert, List, Typography, Space, Spin, Tag, Divider, Row, Col } from "antd";
import { TagsOutlined, InfoCircleOutlined, PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { uldService } from "../../../../api/enhancedApi";
import { useUld } from "../context/UldContext";
import { didBatchSucceed, unwrapBatchOperationResult } from "../../lib/batchOperationResult";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * 批量管理ULD标签的弹窗组件
 */
const TagsBatchManageModal = ({ visible, onCancel, onSuccess, selectedUlds = [] }) => {
    const { tagList } = useUld();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // 获取当前选中ULD的标签分布统计
    const getTagsDistribution = () => {
        const tagCounts = {};
        const allTags = new Set();
        
        selectedUlds.forEach(uld => {
            const tags = uld.master?.tags || [];
            tags.forEach(tag => {
                allTags.add(tag.tag_name);
                tagCounts[tag.tag_name] = (tagCounts[tag.tag_name] || 0) + 1;
            });
        });

        return {
            tagCounts,
            totalUlds: selectedUlds.length,
            uniqueTags: Array.from(allTags)
        };
    };

    const { tagCounts, totalUlds, uniqueTags } = getTagsDistribution();

    // 获取可添加的标签（未被全部ULD拥有的标签）
    const getAvailableTagsToAdd = () => {
        return tagList.filter(tag => {
            const currentCount = tagCounts[tag.tag_name] || 0;
            return currentCount < totalUlds; // 不是所有ULD都有这个标签
        });
    };

    // 获取可移除的标签（至少有一个ULD拥有的标签）
    const getAvailableTagsToRemove = () => {
        return tagList.filter(tag => {
            return tagCounts[tag.tag_name] > 0; // 至少有一个ULD有这个标签
        });
    };

    const availableTagsToAdd = getAvailableTagsToAdd();
    const availableTagsToRemove = getAvailableTagsToRemove();

    useEffect(() => {
        if (visible) {
            // 重置表单
            form.setFieldsValue({
                tags_to_add: [],
                tags_to_remove: [],
                remarks: ""
            });
        }
    }, [visible, form]);

    // 处理提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // 检查是否有实际操作
            if (!values.tags_to_add?.length && !values.tags_to_remove?.length) {
                Modal.warning({
                    title: "操作提示",
                    content: "请选择要添加或移除的标签",
                });
                return;
            }

            // 提取ULD识别码
            const uldIdentifiers = selectedUlds.map(uld => uld.master.uld_identifier);
            
            // 调用批量标签管理API
            const response = await uldService.batchManageTags({
                uld_identifiers: uldIdentifiers,
                tags_to_add: values.tags_to_add || [],
                tags_to_remove: values.tags_to_remove || [],
                remarks: values.remarks
            });

            const result = unwrapBatchOperationResult(response);

            // 显示操作结果
            if (result.success_count === selectedUlds.length) {
                // 全部成功
                Modal.success({
                    title: "操作成功",
                    content: `已成功为 ${result.success_count} 个ULD管理标签`,
                });
            } else if (result.success_count > 0) {
                // 部分成功
                Modal.warning({
                    title: "操作部分成功",
                    content: (
                        <div>
                            <p>成功处理 {result.success_count} 个ULD，失败 {result.failure_count} 个</p>
                            {result.failure_count > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <Text strong>失败项目：</Text>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {(result.failure_items || []).slice(0, 5).map((item, index) => (
                                            <li key={index}>
                                                {item.identifier}: {item.error}
                                            </li>
                                        ))}
                                        {(result.failure_items || []).length > 5 && (
                                            <li>... 还有 {result.failure_items.length - 5} 项失败</li>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ),
                });
            } else {
                // 全部失败
                Modal.error({
                    title: "操作失败",
                    content: "所有ULD标签管理操作均失败，请检查日志或联系系统管理员",
                });
            }

            if (didBatchSucceed(result)) {
                onSuccess?.();
            }

        } catch (error) {
            console.error('批量标签管理失败:', error);
            
            // 安全地处理错误信息
            let errorMessage = "标签管理操作失败";
            
            if (error.response?.data) {
                const errorData = error.response.data;
                if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData.detail) {
                    errorMessage = String(errorData.detail);
                } else if (errorData.message) {
                    errorMessage = String(errorData.message);
                } else {
                    try {
                        errorMessage = JSON.stringify(errorData);
                    } catch {
                        errorMessage = "解析错误信息失败";
                    }
                }
            } else if (error.message) {
                errorMessage = String(error.message);
            }
            
            Modal.error({
                title: "操作失败",
                content: errorMessage,
            });
        } finally {
            setLoading(false);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel?.();
    };

    return (
        <Modal
            title={
                <Space>
                    <TagsOutlined />
                    批量标签管理
                </Space>
            }
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={loading}
            destroyOnHidden
            width={800}
            okText="确认管理"
            cancelText="取消"
        >
            <Form
                form={form}
                layout="vertical"
                preserve={false}
            >
                {/* 当前标签分布展示 */}
                <Alert
                    message="当前标签分布"
                    description={
                        <div>
                            <p>共选择 <Text strong>{totalUlds}</Text> 个ULD</p>
                            {uniqueTags.length > 0 ? (
                                <div>
                                    <Text type="secondary">当前标签分布：</Text>
                                    <div style={{ marginTop: 8 }}>
                                        <Space size={[0, 8]} wrap>
                                            {Object.entries(tagCounts).map(([tagName, count]) => {
                                                const tagDef = tagList.find(t => t.tag_name === tagName);
                                                const percentage = Math.round((count / totalUlds) * 100);
                                                return (
                                                    <Tag 
                                                        key={tagName}
                                                        color={tagDef?.color_code || "#2db7f5"}
                                                        style={{ margin: '2px' }}
                                                    >
                                                        {tagName} ({count}/{totalUlds} - {percentage}%)
                                                    </Tag>
                                                );
                                            })}
                                        </Space>
                                    </div>
                                </div>
                            ) : (
                                <Text type="secondary">选中的ULD暂无标签</Text>
                            )}
                        </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                />

                <Row gutter={16}>
                    <Col span={12}>
                        {/* 添加标签 */}
                        <Form.Item
                            name="tags_to_add"
                            label={
                                <Space>
                                    <PlusOutlined />
                                    添加标签
                                </Space>
                            }
                            tooltip="选择要为所有选中ULD添加的标签"
                        >
                            <Select
                                mode="multiple"
                                placeholder="选择要添加的标签"
                                allowClear
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.children ?? "").toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                disabled={availableTagsToAdd.length === 0}
                                notFoundContent={availableTagsToAdd.length === 0 ? "所有标签已被选中的ULD拥有" : "未找到匹配的标签"}
                            >
                                {availableTagsToAdd.map(tag => {
                                    const currentCount = tagCounts[tag.tag_name] || 0;
                                    const percentage = Math.round((currentCount / totalUlds) * 100);
                                    return (
                                        <Option key={tag.id} value={tag.tag_name}>
                                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                                <Space>
                                                    <Tag 
                                                        color={tag.color_code || "#2db7f5"} 
                                                        style={{ margin: 0, minWidth: 60, textAlign: 'center' }}
                                                    >
                                                        {tag.tag_name}
                                                    </Tag>
                                                    {tag.description && (
                                                        <span style={{ color: '#666' }}>
                                                            {tag.description}
                                                        </span>
                                                    )}
                                                </Space>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {currentCount}/{totalUlds} ({percentage}%)
                                                </Text>
                                            </Space>
                                        </Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                    
                    <Col span={12}>
                        {/* 移除标签 */}
                        <Form.Item
                            name="tags_to_remove"
                            label={
                                <Space>
                                    <CloseOutlined />
                                    移除标签
                                </Space>
                            }
                            tooltip="选择要从所有选中ULD移除的标签"
                        >
                            <Select
                                mode="multiple"
                                placeholder="选择要移除的标签"
                                allowClear
                                disabled={availableTagsToRemove.length === 0}
                                notFoundContent={availableTagsToRemove.length === 0 ? "选中的ULD没有共同标签" : "未找到匹配的标签"}
                            >
                                {availableTagsToRemove.map(tag => {
                                    const currentCount = tagCounts[tag.tag_name] || 0;
                                    const percentage = Math.round((currentCount / totalUlds) * 100);
                                    return (
                                        <Option key={tag.id} value={tag.tag_name}>
                                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                                <Space>
                                                    <Tag 
                                                        color={tag.color_code || "#2db7f5"} 
                                                        style={{ margin: 0, minWidth: 60, textAlign: 'center' }}
                                                    >
                                                        {tag.tag_name}
                                                    </Tag>
                                                    {tag.description && (
                                                        <span style={{ color: '#666' }}>
                                                            {tag.description}
                                                        </span>
                                                    )}
                                                </Space>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {currentCount}/{totalUlds} ({percentage}%)
                                                </Text>
                                            </Space>
                                        </Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                {/* 操作说明 */}
                <Alert
                    message="操作说明"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            <li>添加标签：为选中的所有ULD添加指定标签（如果ULD已有该标签则跳过）</li>
                            <li>移除标签：从选中的所有ULD移除指定标签（如果ULD没有该标签则跳过）</li>
                            <li>可以同时进行添加和移除操作</li>
                            <li>系统会自动记录标签变更历史和操作人员信息</li>
                        </ul>
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                {/* 备注 */}
                <Form.Item
                    name="remarks"
                    label="操作备注"
                    tooltip="记录本次标签管理操作的原因或说明"
                >
                    <TextArea 
                        placeholder="请输入标签管理备注（可选）" 
                        rows={3}
                        maxLength={200}
                        showCount
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default TagsBatchManageModal;
 